function renderFormHookGeneratedFunctionTemplate({
  submissionActionImports,
  actionImportPath,
  pascal,
  camel,
  successMsg,
  hasExportCreateObjectUrl
}: {
  submissionActionImports: string
  actionImportPath: string
  pascal: string
  camel: string
  successMsg: string
  hasExportCreateObjectUrl: string
}): string {
  return `import {
${submissionActionImports}
} from '@admin/actions/${actionImportPath}'
import type {
  Create${pascal}SubmissionInput,
  Get${pascal}SubmissionsFilters
} from '@admin/actions/${actionImportPath}'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

export function use${pascal}Submissions(
  search?: string,
  submittedAtFrom?: string,
  submittedAtTo?: string,
  page?: number,
  pageSize?: number,
  orderBy?: string,
  orderDirection?: 'asc' | 'desc'
) {
  return useQuery({
    queryKey: [
      '${camel}-submissions',
      search ?? '',
      submittedAtFrom ?? '',
      submittedAtTo ?? '',
      page ?? 0,
      pageSize ?? 20,
      orderBy ?? '',
      orderDirection ?? ''
    ],
    queryFn: () => {
      const filters: Get${pascal}SubmissionsFilters = {}

      if (search) filters.search = search
      if (submittedAtFrom) filters.submittedAtFrom = submittedAtFrom
      if (submittedAtTo) filters.submittedAtTo = submittedAtTo
      if (pageSize != null && pageSize !== -1) {
        filters.limit = pageSize
        filters.offset = (page ?? 0) * pageSize
      }
      if (orderBy) filters.orderBy = orderBy
      if (orderDirection) filters.orderDirection = orderDirection

      return get${pascal}Submissions(Object.keys(filters).length > 0 ? filters : undefined)
    }
  })
}

export function use${pascal}Submission(id: string | null) {
  return useQuery({
    queryKey: ['${camel}-submission', id],
    queryFn: () => {
      if (!id) {
        throw new Error('Submission ID is required')
      }
      return get${pascal}Submission(id)
    },
    enabled: !!id
  })
}

export function useCreate${pascal}Submission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Create${pascal}SubmissionInput) => create${pascal}Submission(data),
    onSuccess: async (result) => {
      if (result.success) {
        toast.success('${successMsg}')
        await queryClient.refetchQueries({ queryKey: ['${camel}-submissions'] })
      } else {
        toast.error(result.error || 'Failed to submit form')
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to submit form')
    }
  })
}

export function useDelete${pascal}Submission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => delete${pascal}Submission(id),
    onSuccess: async () => {
      toast.success('Submission deleted successfully')
      await queryClient.refetchQueries({ queryKey: ['${camel}-submissions'] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete submission')
    }
  })
}
${hasExportCreateObjectUrl}`
}

function renderFormHookGeneratedFunctionDetailsTemplate({
  pascal,
  formName
}: {
  pascal: string
  formName: string
}): string {
  return `
export function useExport${pascal}SubmissionsCSV() {
  return useMutation({
    mutationFn: export${pascal}SubmissionsCSV,
    onSuccess: (csvContent) => {
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = \`${formName}-submissions-\${new Date().toISOString().split('T')[0]}.csv\`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('CSV exported successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to export CSV')
    }
  })
}
`
}

export function renderFormPipelineFormHookRenderer(
  request:
    | {
        kind: 'renderFormHookGeneratedFunctionTemplate'
        props: {
          submissionActionImports: string
          actionImportPath: string
          pascal: string
          camel: string
          successMsg: string
          hasExportCreateObjectUrl: string
        }
      }
    | {
        kind: 'renderFormHookGeneratedFunctionDetailsTemplate'
        props: {
          pascal: string
          formName: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderFormHookGeneratedFunctionTemplate':
      return renderFormHookGeneratedFunctionTemplate(request.props)
    case 'renderFormHookGeneratedFunctionDetailsTemplate':
      return renderFormHookGeneratedFunctionDetailsTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
